import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Drawer, Flex, Portal, Text } from "@chakra-ui/react";

import { selectors, actions } from "@shared/store";
import { MODAL_TYPES, OVERLAY_TYPES } from "@shared/constants";
import { OverlaySize } from "@shared/interfaces";
import { CaseFormView } from "@containers/Cases/components/CaseViewContainer";
import { CasePrisonMigrationAnalysisView } from "@containers/Cases/components/CaseViewContainer/CasePrisonMigrationAnalysisView";
import { RunEvalOverlay } from "@containers/Evaluation/components";

import { CloseButton } from "../CloseButton";
import { PdfView } from "../PdfView";

function OverlayWrapper() {
  const dispatch = useDispatch();

  const overlay = useSelector(selectors.getOverlay());

  const hideOverlayAction = useCallback(() => {
    dispatch(actions.hideOverlay());
    if (overlay?.props.onClose) {
      overlay.props.onClose();
    }
  }, [dispatch, overlay]);

  const onCloseOverlay = useCallback(() => {
    if (overlay?.leaveConfirm) {
      dispatch(
        actions.showModal({
          type: MODAL_TYPES.CONFIRM,
          props: {
            heading: "You have unsaved changes",
            content: "Are you sure you want to leave? If you leave this page now your progress will be lost.",
            cancelBtnText: "Cancel",
            successBtnText: "Leave",
            onSuccess: hideOverlayAction,
          },
        }),
      );
      return;
    }

    hideOverlayAction();
  }, [overlay, dispatch, hideOverlayAction]);

  const Component = overlay ? OVERLAY_COMPONENTS[overlay.type as keyof typeof OVERLAY_COMPONENTS] : undefined;

  const overlayTitle = overlay?.title;

  return overlay ? (
    <>
      <Drawer.Root
        open={Boolean(overlay)}
        placement="end"
        onOpenChange={({ open }) => !open && onCloseOverlay()}
        closeOnInteractOutside={overlay.closeOnOverlayClick}
      >
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content
              w={overlay?.size === OverlaySize.FULLSCREEN ? "calc(100% - 65px)" : "550px"}
              maxW={overlay?.size === OverlaySize.FULLSCREEN ? "calc(100% - 65px)" : "550px"}
              bg="white"
            >
              <Flex justify={overlayTitle ? "space-between" : "flex-end"} align="center" px={5} py={2.5}>
                {overlayTitle && <Text textStyle="overlayTitle">{overlayTitle}</Text>}
                <CloseButton onClick={hideOverlayAction} />
              </Flex>
              <Drawer.Body>{Component ? <Component {...overlay.props} onClose={onCloseOverlay} /> : null}</Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    </>
  ) : null;
}

export default OverlayWrapper;

const OVERLAY_COMPONENTS = {
  [OVERLAY_TYPES.SHOW_PDF]: PdfView,
  [OVERLAY_TYPES.SHOW_CASE_FORM]: CaseFormView,
  [OVERLAY_TYPES.SHOW_PRISON_MIGRATION_ANALYSIS]: CasePrisonMigrationAnalysisView,
  [OVERLAY_TYPES.SHOW_RUN_EVAL]: RunEvalOverlay,
};
